import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { getServices, getServiceCategories } from '../services/services'
import { getWorkers } from '../services/workers'
import { fetchAvailability, fetchAllAvailability } from '../services/availability'
import './Booking.css'
import Services from '../components/Services'
import BookingProgressBar from '../components/BookingProgressBar'
import BookingSidebar from '../components/BookingSidebar'
import WorkerSelection from '../components/WorkerSelection'
import DateTimeSelection from '../components/DateTimeSelection'
import BookingConfirmation from '../components/BookingConfirmation'
import { createAppointment, updateAppointment, getAppointment, getAppointmentsByDate } from '../services/appointments'
import { loadStripe } from '@stripe/stripe-js';
import { createPaymentIntent } from '../services/stripe'
import { useSearchParams } from 'react-router-dom'
import { getServiceById } from '../services/services'

const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY

// Initialize Stripe with error handling
const initStripe = () => {
    // console.log('Initializing Stripe with key:', STRIPE_KEY);
  if (!STRIPE_KEY) {
    console.error('REACT_APP_STRIPE_KEY is not defined in environment variables');
    return null;
  }
  return loadStripe(STRIPE_KEY);
};

const stripePromise = initStripe();

function Booking() {
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get('businessId');
  const selectedServiceId = searchParams.get('selectedServiceId')
  const isEditMode = searchParams.get('edit') === 'true'
  const editAppointmentId = searchParams.get('appointmentId')
  const navigate = useNavigate()
  const location = useLocation()
  
  // Redirect if no businessId is provided
  useEffect(() => {
    if (!businessId) {
      console.error('No businessId provided in URL parameters')
      navigate('/')
      return
    }
  }, [businessId, navigate])
  
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedService, setSelectedService] = useState(null)
  const [selectedWorker, setSelectedWorker] = useState(null)
  const [selectedTime, setSelectedTime] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [currentDateIndex, setCurrentDateIndex] = useState(0)
  const [showCalendar, setShowCalendar] = useState(false)
  
  // Form data states (removed notes)
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    contact_number: ''
  })
  
  // Data states
  const [services, setServices] = useState([])
  const [categories, setCategories] = useState([])
  const [workers, setWorkers] = useState([])
  const [availableTimes, setAvailableTimes] = useState([])
  const [availabilityData, setAvailabilityData] = useState({})
  const [clientSecret, setClientSecret] = useState('')
  const [paymentIntent, setPaymentIntent] = useState("")
  const [originalAppointmentData, setOriginalAppointmentData] = useState(null)
  const [existingAppointments, setExistingAppointments] = useState([])
  const [isAutoSelectingWorker, setIsAutoSelectingWorker] = useState(false)
  const [paymentCompleted, setPaymentCompleted] = useState(false)
  const [isLoadingEditData, setIsLoadingEditData] = useState(isEditMode && editAppointmentId ? true : false)


  const fetchSecret = async () => {
    if (!businessId) {
      console.error('BusinessId is required for creating payment intent')
      return
    }

    if (!selectedService.has_deposit || selectedService.deposit_fraction === 0) {
      return
    }
    
    // Don't create a new payment intent if payment has already been completed
    if (paymentCompleted) {
      // console.log('Payment already completed, skipping payment intent creation')
      return
    }
    
    try {
      // console.log(selectedService)
      const data = await createPaymentIntent(businessId,{
        id: selectedService.id,
        amount: selectedService.has_deposit ? Number(selectedService.price * selectedService.deposit_fraction) : Number(selectedService.price)
      })
      setClientSecret(data.clientSecret)
      setPaymentIntent(data.paymentIntentId)
    } catch (error) {
      console.error('Error creating payment intent:', error)
    }
  }

  // Check for appointment overlap and end-of-day constraints
  const checkTimeSlotOverlap = (selectedTimeString, selectedDateString, serviceDuration) => {
    // Convert selected time to Date object
    const selectedDateTime = new Date(`${selectedDateString}T${convertTo24Hour(selectedTimeString)}:00`);
    const selectedEndTime = new Date(selectedDateTime.getTime() + (serviceDuration * 60000));

    // Check if appointment extends past business hours
    if (availabilityData && availabilityData[selectedDateString]) {
      const dayIntervals = availabilityData[selectedDateString];
      
      // Find the latest available time slot for this day
      let latestEndTime = null;
      for (const interval of dayIntervals) {
        if (interval.available) {
          const intervalEndTime = new Date(`${selectedDateString}T${interval.end_time}:00`);
          if (!latestEndTime || intervalEndTime > latestEndTime) {
            latestEndTime = intervalEndTime;
          }
        }
      }
      
      // Check if the appointment would end after business hours
      if (latestEndTime && selectedEndTime > latestEndTime) {
        return { type: 'endOfDay', message: `This appointment would extend past business hours. The service (${serviceDuration} minutes) would end at ${selectedEndTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}, but the business closes at ${latestEndTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}.` };
      }
    }

    // Check against existing appointments if any
    if (existingAppointments && existingAppointments.length > 0) {
      for (const appointment of existingAppointments) {
        const appointmentStart = new Date(appointment.start);
        const appointmentEnd = new Date(appointment.end);

        // Check if times overlap
        if (selectedDateTime < appointmentEnd && selectedEndTime > appointmentStart) {
          return { type: 'overlap', message: `This time slot conflicts with an existing appointment. The selected service (${serviceDuration} minutes) would overlap with another booking. Please choose a different time.` };
        }
      }
    }

    return false; // No conflicts
  }

  // Helper function to convert 12-hour time to 24-hour format
  const convertTo24Hour = (time12h) => {
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');
    if (hours === '12') {
      hours = '00';
    }
    if (modifier === 'PM') {
      hours = (parseInt(hours, 10) + 12).toString();
    }
    return `${hours.padStart(2, '0')}:${minutes}`;
  }


  // Generate 6 months of dates
  const generateDates = () => {
    const dates = []
    const today = new Date()
    for (let i = 0; i < 180; i++) { // 6 months ~180 days
      const date = new Date(today)
      date.setDate(today.getDate() + i)
      dates.push(date)
    }
    return dates
  }

  const allDates = generateDates()

  useEffect(() => {
    const fetchData = async () => {
      if (!businessId) {
        console.error('BusinessId is required but not provided')
        return
      }
      
      try {

        if (!(isEditMode && editAppointmentId)) {
          const servicesData = await getServices(businessId)
          const categoriesData = await getServiceCategories(businessId)

          console.log('Fetched services data:', servicesData);
          console.log('Fetched categories data:', categoriesData);
          setServices(Array.isArray(servicesData) ? servicesData : [])
          setCategories(Array.isArray(categoriesData) ? categoriesData : [])
        }
        // Handle edit mode - load existing appointment data
        if (isEditMode && editAppointmentId) {
          setIsLoadingEditData(true)
          try {
            // Get workerId from URL params if available, otherwise try to get from appointment
            const urlWorkerId = searchParams.get('workerId')
            const appointmentData = await getAppointment(editAppointmentId, businessId, urlWorkerId || 'any')
            setOriginalAppointmentData(appointmentData)
            
            // Pre-populate form with existing data
            setCustomerInfo(appointmentData.client_data || {})
            console.log('Loaded appointment data for editing:', appointmentData.client_data)

            // Load and set the service
            if (appointmentData.service_id) {
              setSelectedService(appointmentData.service)
              
              // Fetch workers for this service
              const workersData = await getWorkers(businessId, appointmentData.service_id)
              setWorkers(Array.isArray(workersData) ? workersData : [])
            }

            // Set the worker if available
            if (appointmentData.worker_id) {
              setSelectedWorker(appointmentData.worker)
            }

            // Parse and set date/time from appointment start datetime
            if (appointmentData.start) {
              const appointmentDate = new Date(appointmentData.start)
              const dateString = appointmentDate.toISOString().split('T')[0] // YYYY-MM-DD
              const timeString = appointmentDate.toLocaleTimeString('en-US', { 
                hour: 'numeric', 
                minute: '2-digit', 
                hour12: true 
              })
              setSelectedDate(dateString)
              setSelectedTime(timeString)
            }

            // Start at step 2 for edit mode (skip service selection)
            setCurrentStep(2)
            setIsLoadingEditData(false)
          } catch (error) {
            console.error('Error loading appointment for editing:', error)
            alert('Failed to load appointment data for editing')
            setIsLoadingEditData(false)
          }
        } else {
          // Handle pre-selected service from navigation state (normal booking flow)
          const { preSelectedService, skipToStep } = location.state || {}
          if (preSelectedService && !selectedServiceId) {
            setSelectedService(preSelectedService)
            if (skipToStep) {
              setCurrentStep(skipToStep)
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }
    fetchData()
  }, [businessId, selectedServiceId, location.state, isEditMode, editAppointmentId])

  // Handle preselected service from URL parameters
  useEffect(() => {
    const fetchPreselectedService = async () => {
      if (!businessId || !selectedServiceId) {
        return
      }
      
      try {
        // console.log('Fetching preselected service with ID:', selectedServiceId)
        const preSelectedService = await getServiceById(businessId, selectedServiceId)
        // console.log('Preselected service fetched:', preSelectedService)
        setSelectedService(preSelectedService)
      } catch (error) {
        console.error('Error fetching preselected service:', error)
        console.error('BusinessId:', businessId, 'ServiceId:', selectedServiceId)
      }
    }
    
    fetchPreselectedService()
  }, [businessId, selectedServiceId])


  // Fetch availability data when service and worker are selected
  useEffect(() => {
    const fetchAvailabilityData = async () => {
      if (!businessId) {
        console.error('BusinessId is required for fetching availability')
        return
      }
      
      // Skip re-fetching if we're auto-selecting a worker
      if (isAutoSelectingWorker) {
        setIsAutoSelectingWorker(false);
        return;
      }
      
      if (selectedService && selectedWorker) {
        try {
          let availabilityResponse;
          
          // Fetch availability based on selected worker
          if (selectedWorker.id === 'any') {
            // console.log('Fetching all availability for any worker...')
            availabilityResponse = await fetchAllAvailability(businessId, selectedService.id)
          } else {
            // console.log(`Fetching availability for worker ${selectedWorker.id}...`)
            availabilityResponse = await fetchAvailability(businessId, selectedWorker.id, selectedService.id)
          }

          // console.log('Availability response:', availabilityResponse)

          // Store the raw availability data - now using intervals_by_date
          const intervalsByDate = availabilityResponse.intervals_by_date || {}
          setAvailabilityData(intervalsByDate)
          
          // If a date is already selected, update available times for that date
          if (selectedDate && Object.keys(intervalsByDate).length > 0) {
            updateAvailableTimesForDate(selectedDate, intervalsByDate)
          } else if (Object.keys(intervalsByDate).length === 0) {
            // Clear times if no availability data
            setAvailableTimes([])
          }
        } catch (error) {
          console.error('Error fetching availability:', error)
          setAvailabilityData({})
          setAvailableTimes([])
        }
      } else {
        // Clear availability data if service or worker not selected
        setAvailabilityData({})
        setAvailableTimes([])
      }
    }

    fetchAvailabilityData()
  }, [businessId, selectedService, selectedWorker])

  // Fetch existing appointments when worker and date are selected
  useEffect(() => {
    const fetchExistingAppointments = async () => {
      if (!businessId || !selectedWorker || !selectedDate || selectedWorker.id === 'any') {
        setExistingAppointments([]);
        return;
      }

      try {
        // console.log(`Fetching existing appointments for worker ${selectedWorker.id} on ${selectedDate}...`);
        const appointments = await getAppointmentsByDate(businessId, selectedWorker.id, selectedDate);
        setExistingAppointments(Array.isArray(appointments) ? appointments : []);
      } catch (error) {
        console.error('Error fetching existing appointments:', error);
        setExistingAppointments([]);
      }
    };

    fetchExistingAppointments();
  }, [businessId, selectedWorker, selectedDate]);

  // Update available times when date is selected
  useEffect(() => {
    if (selectedDate && Object.keys(availabilityData).length > 0) {
      // Check if the selected date has any intervals (is not closed)
      const dayIntervals = availabilityData[selectedDate] || []
      if (dayIntervals.length === 0) {
        // Clear the selection if the date is closed
        setSelectedDate(null)
        setAvailableTimes([])
      } else {
        updateAvailableTimesForDate(selectedDate, availabilityData)
      }
    }
  }, [selectedDate, availabilityData])

  // Helper function to format time from 24-hour to 12-hour format
  const formatTimeTo12Hour = (time24) => {
    const [hours, minutes] = time24.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${hour12}:${minutes} ${ampm}`
  }

  // Helper function to update available times for a specific date
  const updateAvailableTimesForDate = (dateString, intervalsByDate) => {
    // console.log('updateAvailableTimesForDate called with:')
    // console.log('dateString:', dateString)
    // console.log('intervalsByDate keys:', Object.keys(intervalsByDate))
    
    // dateString should already be in YYYY-MM-DD format
    const formattedSelectedDate = dateString
    
    // console.log('Looking for date:', formattedSelectedDate)
    // console.log('Available dates in data:', Object.keys(intervalsByDate))
    
    // Get intervals for the selected date from the intervals_by_date object
    const dayIntervals = intervalsByDate[formattedSelectedDate] || []

    // console.log('Found intervals for selected date:', dayIntervals.length, 'intervals')
    
    // Create a map of all time slots with their availability status
    const timeSlotMap = new Map()
    
    dayIntervals.forEach(interval => {
      const formattedTime = formatTimeTo12Hour(interval.start_time)
      // If we already have this time slot, keep it available if any interval is available
      if (timeSlotMap.has(formattedTime)) {
        const existing = timeSlotMap.get(formattedTime)
        timeSlotMap.set(formattedTime, {
          time: formattedTime,
          available: existing.available || interval.available,
          rawTime: interval.start_time
        })
      } else {
        timeSlotMap.set(formattedTime, {
          time: formattedTime,
          available: interval.available,
          rawTime: interval.start_time
        })
      }
    })
    
    // Convert map to array and sort by time
    const timeSlots = Array.from(timeSlotMap.values()).sort((a, b) => {
      const timeA = new Date(`1970/01/01 ${a.time}`)
      const timeB = new Date(`1970/01/01 ${b.time}`)
      return timeA - timeB
    })
    
    // console.log('Time slots with availability:', timeSlots)
    setAvailableTimes(timeSlots)
  }

  const handleCustomerInfoChange = (field, value) => {
    setCustomerInfo(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const convertToPostgresTimestamp = (dateString, timeString) => {
    const date = new Date(dateString)
    const [time, period] = timeString.split(' ')
    const [hours, minutes] = time.split(':').map(Number)
    
    let hour24 = hours
    if (period === 'PM' && hours !== 12) {
      hour24 = hours + 12
    } else if (period === 'AM' && hours === 12) {
      hour24 = 0
    }
    
    date.setHours(hour24, minutes, 0, 0)
    return date.toISOString()
  }

  const calculateEndTime = (startTimestamp, durationMinutes) => {
    const startTime = new Date(startTimestamp)
    const endTime = new Date(startTime.getTime() + (durationMinutes * 60000))
    return endTime.toISOString()
  }

  const handleServiceSelect = (service) => {
    setSelectedService(service)
    // Don't auto-advance step, let user click continue
  }

  // Fetch workers when a service is selected
  useEffect(() => {

    const fetchWorkersForService = async () => {
      if (selectedService && businessId) {
        try {
          const workersData = selectedService.workers
          console.log('Fetched workers for service:', workersData);
          setWorkers(Array.isArray(workersData) ? workersData : [])
        } catch (error) {
          console.error('Error fetching workers for service:', error)
          setWorkers([])
        }
      }
    }
    
    fetchWorkersForService()
  }, [selectedService, businessId])

  const handleWorkerSelect = (worker) => {
    setSelectedWorker(worker)
    // Reset time and date selection when worker changes since availability will be different
    setSelectedTime(null)
    setSelectedDate(null)
    // Clear current availability data to trigger fresh fetch
    setAvailableTimes([])
  }

  const handleTimeSelect = (time, date) => {
    // If clicking the same time, unselect it
    if (selectedTime === time && selectedDate === date) {
      setSelectedTime(null)
      return
    }

    // Check for appointment overlap and business hours before selecting the time
    if (selectedService) {
      const conflict = checkTimeSlotOverlap(time, date, selectedService.duration);
      if (conflict) {
        alert(conflict.message);
        return; // Don't select the time if there's a conflict
      }
    }

    // Auto-select an available worker if "All" is selected
    if (selectedWorker && selectedWorker.id === 'any') {
      // Find the time slot in availability data to get the selected worker
      const dayIntervals = availabilityData[date] || [];
      const selectedTimeSlot = dayIntervals.find(interval => {
        const intervalStartTime = new Date(`${date}T${interval.start_time}:00`);
        const selectedDateTime = new Date(`${date}T${convertTo24Hour(time)}:00`);
        return intervalStartTime.getTime() === selectedDateTime.getTime();
      });

      if (selectedTimeSlot && selectedTimeSlot.worker_availability && selectedTimeSlot.worker_availability.selected_worker) {
        // Get the selected worker from the new API structure
        const selectedWorkerId = selectedTimeSlot.worker_availability.selected_worker.worker_id;
        
        // Find the worker object from the workers array
        const availableWorker = workers.find(worker => worker.id === selectedWorkerId);
        
        if (availableWorker) {
          // console.log(`Auto-selecting available worker: ${availableWorker.name} (ID: ${availableWorker.id})`);
          // console.log(`Selection reason: ${selectedTimeSlot.worker_availability.selected_worker.selection_reason}`);
          setIsAutoSelectingWorker(true); // Prevent availability re-fetch
          setSelectedWorker(availableWorker);
        }
      }
    }

    setSelectedTime(time)
    setSelectedDate(date)
    // Don't auto-advance step, let user click continue
  }

  const handleContinue = async () => {
    if (currentStep === 3 && !isEditMode) {
      // Only fetch payment secret for new bookings
      await fetchSecret()
    }
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1)
    } else if (isEditMode && currentStep === 3) {
      // For edit mode, skip payment and go directly to confirmation
      await handleConfirmBooking()
    }
  }

  const handleConfirmBooking = async () => {
    if (!businessId) {
      alert('BusinessId is required but not provided')
      return null
    }
    
    if (!customerInfo.name || !customerInfo.email || !customerInfo.contact_number) {
      alert('Please fill in all required fields (Name, Email, Phone Number)')
      return null
    }

    // Final check for appointment overlap and business hours before confirming
    if (!isEditMode && selectedService && selectedWorker.id !== 'any') {
      const conflict = checkTimeSlotOverlap(selectedTime, selectedDate, selectedService.duration);
      if (conflict) {
        alert(conflict.message);
        return null;
      }
    }

    const startTimestamp = convertToPostgresTimestamp(selectedDate, selectedTime)
    const endTimestamp = calculateEndTime(startTimestamp, selectedService.duration)
    // console.log("payment intent: ", isEditMode ? originalAppointmentData?.payment_intent : clientSecret)

    try {
      const appointmentData = {
        start: startTimestamp,
        end: endTimestamp,
        service_id: selectedService.id,
        worker_id: selectedWorker.id === 'any' ? null : selectedWorker.id,
        business_id: businessId,
        payment_intent: isEditMode ? originalAppointmentData?.payment_intent : paymentIntent,
        payment_amount: selectedService.has_deposit ? parseFloat(selectedService.price * selectedService.deposit_fraction).toFixed(2) : 0,
        client_data: {
          name: customerInfo.name,
          contact_number: customerInfo.contact_number,
          email: customerInfo.email,
        }
      }

      if (isEditMode && editAppointmentId) {
        // Update existing appointment
        // console.log('Updating appointment with data:', appointmentData)
        const workerId = originalAppointmentData?.worker_id || (selectedWorker.id === 'any' ? null : selectedWorker.id)
        const updatedAppointment = await updateAppointment(businessId, workerId, editAppointmentId, appointmentData)
        // console.log('Appointment updated successfully:', updatedAppointment)
        
        // Check if the API returned an error due to 12-hour restriction
        if (updatedAppointment && updatedAppointment.success === false) {
          alert(updatedAppointment.message || 'Cannot edit appointment within 12 hours of its start time')
          return null
        }
        
        // Navigate directly to confirmation page for edit mode (no payment needed)
        navigate(`/confirm?businessId=${businessId}&appointmentId=${editAppointmentId}&serviceId=${selectedService.id}`)
        return updatedAppointment
      } else {
        // Create new appointment
        // console.log('Creating appointment with data:', appointmentData)
        const createdAppointment = await createAppointment(businessId, selectedWorker.id === 'any' ? null : selectedWorker.id, appointmentData)
        // console.log('Appointment created successfully:', createdAppointment)
        
        // Return the created appointment data so PaymentForm can access the ID
        return createdAppointment
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} appointment:`, error)
      alert(`Error ${isEditMode ? 'updating' : 'creating'} appointment. Please try again.`)
      return null
    }
  }

  const goBack = () => {
    const minStep = isEditMode ? 2 : 1;
    
    if (currentStep > minStep) {
      setCurrentStep(currentStep - 1)
      // Keep all selections in state - BookingSidebar will handle display logic
    } else {
      // Go back to previous page or confirmation page
      if (isEditMode) {
        // Navigate back to appointment confirmation
        navigate(`/confirm?businessId=${businessId}&appointmentId=${editAppointmentId}&workerId=${originalAppointmentData?.worker_id}`)
      } else {
        // Go back to home page from first step
        navigate('/')
      }
    }
  }

  const scrollDatesLeft = () => {
    setCurrentDateIndex(Math.max(0, currentDateIndex - 7))
  }

  const scrollDatesRight = () => {
    setCurrentDateIndex(Math.min(allDates.length - 7, currentDateIndex + 7))
  }

  const getCurrentMonthYear = () => {
    const visibleDates = allDates.slice(currentDateIndex, currentDateIndex + 7)
    if (visibleDates.length === 0) return ''
    
    const firstDate = visibleDates[0]
    const lastDate = visibleDates[visibleDates.length - 1]
    
    if (firstDate.getMonth() === lastDate.getMonth()) {
      return firstDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    } else {
      return `${firstDate.toLocaleDateString('en-US', { month: 'short' })} - ${lastDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
    }
  }

  const handleCalendarDateSelect = (date) => {
    const isoDateString = date.toISOString().split('T')[0] // Convert to YYYY-MM-DD format
    setSelectedDate(isoDateString)
    setShowCalendar(false)
    // Find the index of selected date and update currentDateIndex to show it
    const dateIndex = allDates.findIndex(d => d.toDateString() === date.toDateString())
    if (dateIndex !== -1) {
      setCurrentDateIndex(Math.max(0, dateIndex - 3)) // Center the selected date
    }
  }

  const renderServiceSelection = () => (
    <div className="booking-step">
      <h2>Select a Service</h2>
      <Services 
        mode="booking"
        onServiceSelect={handleServiceSelect}
        services={services}
        categories={categories}
        selectedService={selectedService}
        businessId={businessId}
      />
    </div>
  )

  return (
    <div className="booking-container">
      {isLoadingEditData ? (
        <div className="booking-loading-container">
          <div className="booking-loading-spinner"></div>
          <p>Loading appointment details...</p>
        </div>
      ) : (
        <>
      
        {((currentStep > 1 && !isEditMode) || (currentStep > 2 && isEditMode)) && (
          <button className="back-btn m-2" onClick={goBack}>
            &#8249; Back
          </button>
        )}

      <div className="booking-header">
        <h1>{isEditMode ? 'Edit Your Appointment' : 'Book Your Appointment'}</h1>
        <BookingProgressBar currentStep={currentStep} isEditMode={isEditMode} />
      </div>

      <div className={`booking-main-content ${currentStep === 4 ? 'centered' : ''}`}>
        <div className="booking-content">
          {currentStep === 1 && !isEditMode && renderServiceSelection()}
          {currentStep === 2 && (
            <WorkerSelection 
              workers={workers} 
              onWorkerSelect={handleWorkerSelect}
              selectedWorker={selectedWorker}
            />
          )}
          {currentStep === 3 && (
            <DateTimeSelection
              workers={workers}
              selectedWorker={selectedWorker}
              onWorkerSelect={handleWorkerSelect}
              allDates={allDates}
              currentDateIndex={currentDateIndex}
              onScrollDatesLeft={scrollDatesLeft}
              onScrollDatesRight={scrollDatesRight}
              getCurrentMonthYear={getCurrentMonthYear}
              onShowCalendar={() => setShowCalendar(true)}
              showCalendar={showCalendar}
              onCloseCalendar={() => setShowCalendar(false)}
              onCalendarDateSelect={handleCalendarDateSelect}
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
              availableTimes={availableTimes}
              selectedTime={selectedTime}
              onTimeSelect={handleTimeSelect}
              availabilityData={availabilityData}
              selectedService={selectedService}
            />
          )}
          {currentStep === 4 && (
            isEditMode ? (
              <div className="booking-step">
                <h2>Confirm Changes</h2>
                <div className="edit-confirmation-layout">
                  <div className="summary-card">
                    <h3>Updated Appointment Details</h3>
                    <div className="summary-item">
                      <span><strong>Service:</strong></span>
                      <span>{selectedService?.service || selectedService?.service_name}</span>
                    </div>
                    <div className="summary-item">
                      <span><strong>Duration:</strong></span>
                      <span>{selectedService?.duration} minutes</span>
                    </div>
                    <div className="summary-item">
                      <span><strong>Stylist:</strong></span>
                      <span>{selectedWorker?.name}</span>
                    </div>
                    <div className="summary-item">
                      <span><strong>Date:</strong></span>
                      <span>{new Date(selectedDate).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                    <div className="summary-item">
                      <span><strong>Time:</strong></span>
                      <span>{selectedTime}</span>
                    </div>
                    <div className="summary-item">
                      <span><strong>Name:</strong></span>
                      <span>{customerInfo.name}</span>
                    </div>
                    <div className="summary-item">
                      <span><strong>Email:</strong></span>
                      <span>{customerInfo.email}</span>
                    </div>
                    <div className="summary-item">
                      <span><strong>Phone:</strong></span>
                      <span>{customerInfo.contact_number}</span>
                    </div>
                    <div className="summary-item total">
                      <span><strong>Total:</strong></span>
                      <span>£{selectedService?.price}</span>
                    </div>
                  </div>
                    <p className="edit-note">*No additional payment required for appointment changes.</p>
                  <div className="edit-actions">
                    <button className="confirm-btn mt-3" onClick={handleConfirmBooking}>
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <BookingConfirmation
                customerInfo={customerInfo}
                onCustomerInfoChange={handleCustomerInfoChange}
                onConfirmBooking={handleConfirmBooking}
                selectedService={selectedService}
                selectedWorker={selectedWorker}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                clientSecret={clientSecret}
                stripePromise={stripePromise}
                businessId={businessId}
                paymentCompleted={paymentCompleted}
                onPaymentCompleted={setPaymentCompleted}
                showPaymentForm={selectedService.has_deposit && selectedService.deposit_fraction > 0}
              />
            )
          )}
        </div>
        
        {currentStep !== 4 && (
          <>
          <div className="booking-sidebar">
            <BookingSidebar
              selectedService={selectedService}
              selectedWorker={selectedWorker}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              currentStep={currentStep}
              onContinue={handleContinue}
            />
          </div>
          {/* Sticky Bottom Continue Bar */}
          <div className="booking-sticky-bottom-bar">
            <div className="bottom-bar-content">
              <div className="selected-summary">
                {selectedService && (
                  <div className="service-summary">
                    <span className="service-name">{selectedService.service || selectedService.service_name}</span>
                    <span className="service-price">£{selectedService.price}</span>
                  </div>
                )}
              </div>
              <button 
                className="continue-btn"
                onClick={handleContinue}
                disabled={!selectedService || (currentStep === 2 && !selectedWorker) || (currentStep === 3 && (!selectedDate || !selectedTime))}
              >
                Continue
              </button>
            </div>
          </div>
          </>
        )}
      </div>
      
      </>
      )}
    </div>
  )
}

export default Booking